import { useEffect } from 'react';

/**
 * Keep the compact attribution collapsed unless the user opens it.
 *
 * MapLibre re-runs _updateAttributions() whenever the attribution text changes,
 * and that re-applies `maplibregl-compact-show`. Every base map declares its own
 * attribution, so switching layers pops the panel open again - which, with a
 * long DEA/Copernicus credit, covers a chunk of the map each time.
 *
 * Two things make this fiddlier than it looks:
 *
 *  - The control is added by MapLibre after this effect first runs, so we have
 *    to wait for it rather than querying once.
 *  - The ⓘ button opens via the same class we are stripping, so we cannot just
 *    remove it on sight. We read the class back after a click to learn what the
 *    user actually wanted, and only force-collapse when they did not ask.
 */
const SHOW = 'maplibregl-compact-show';

export function useCollapsedAttribution() {
  useEffect(() => {
    let el = null;
    let userOpen = false;
    let ours = false;
    let classObs = null;
    let cancelled = false;

    const forceCollapsed = () => {
      if (!el || userOpen || !el.classList.contains(SHOW)) return;
      ours = true;
      el.classList.remove(SHOW);
    };

    const onClick = (e) => {
      if (!e.target.closest('.maplibregl-ctrl-attrib-button')) return;
      // Record intent SYNCHRONOUSLY. A MutationObserver fires as a microtask, so
      // deferring this (even to setTimeout 0) lets the observer strip the class
      // before we know the user asked for it - the button then does nothing.
      // Capture phase, so this runs BEFORE MapLibre's own button handler and
      // reads the pre-toggle state. On bubble it would read the state MapLibre
      // had already flipped, and record the exact opposite of what was wanted.
      userOpen = !el.classList.contains(SHOW);
    };

    const attach = (node) => {
      el = node;
      el.addEventListener('click', onClick, true);
      classObs = new MutationObserver(() => {
        if (ours) { ours = false; return; }
        forceCollapsed();
      });
      classObs.observe(el, { attributes: true, attributeFilter: ['class'] });
      forceCollapsed();
    };

    const existing = document.querySelector('.maplibregl-ctrl-attrib');
    if (existing) {
      attach(existing);
    } else {
      // Poll briefly - the control appears once the map finishes loading.
      const started = Date.now();
      const tick = () => {
        if (cancelled) return;
        const node = document.querySelector('.maplibregl-ctrl-attrib');
        if (node) return attach(node);
        if (Date.now() - started < 30000) setTimeout(tick, 250);
      };
      tick();
    }

    return () => {
      cancelled = true;
      if (classObs) classObs.disconnect();
      if (el) el.removeEventListener('click', onClick, true);
    };
  }, []);
}

export default useCollapsedAttribution;
